import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";
import {
  useDispatchById,
  useCompleteDispatch,
  useConfirmDelivery,
  useRecordTrayReturn,
  useRaiseCompensation,
} from "../hooks/useDispatch";
import { useDrivers } from "../hooks/useDrivers";
import { dispatchApi } from "../services/dispatchApi";
import { PermissionGate } from "@/shared/components/PermissionGate";
import {
  tripGuidanceApi,
  type TripRouteSnapshotDto,
  type AssignRouteToTripRequest,
} from "../services/tripGuidanceApi";
import { routeTemplatesApi, type RouteTemplateSummaryDto } from "../services/routeTemplatesApi";

const formatDate = (v?: string) => (v ? new Date(v).toLocaleDateString("en-KE") : "—");
const formatDateTime = (v?: string) =>
  v ? new Date(v).toLocaleString("en-KE", { dateStyle: "short", timeStyle: "short" }) : "—";

const getDriverDisplayName = (driver?: { fullName?: string; phone?: string }) => {
  const fullName = driver?.fullName?.trim();
  if (fullName) return fullName;
  const phone = driver?.phone?.trim();
  return phone ? `Driver (${phone})` : "Driver";
};

const getUnitLabel = (unitName?: string) => {
  if (!unitName) return "Units";
  const normalized = unitName.trim().toLowerCase();
  if (normalized.includes("piece")) return "Pieces";
  if (normalized.includes("tray")) return "Trays";
  return unitName;
};

const formatTrayType = (trayType?: string) => {
  if (!trayType) return "—";
  const map: Record<string, string> = {
    TwoHundredTenHole: "210-hole",
    FourFortyTwoHole: "442-hole",
  };
  return map[trayType] ?? trayType;
};

const formatKES = (amount: number) =>
  new Intl.NumberFormat("en-KE", { style: "currency", currency: "KES", maximumFractionDigits: 2 }).format(amount);

function statusBadge(status: string) {
  const labels: Record<string, string> = {
    Pending: "Pending",
    HandedToDispatch: "Handed to Dispatch",
    ReadyForRelease: "Ready for Release",
    Dispatched: "Dispatched",
    Delivered: "Delivered",
  };
  const variants: Record<string, "default" | "secondary" | "outline"> = {
    Pending: "outline",
    HandedToDispatch: "secondary",
    ReadyForRelease: "secondary",
    Dispatched: "default",
    Delivered: "default",
  };
  return <Badge variant={variants[status] ?? "outline"}>{labels[status] ?? status}</Badge>;
}

// ── Complete Dispatch Dialog ───────────────────────────────────────────────
function CompleteDispatchDialog({
  id,
  mode,
  customerName,
  orderNumber,
  totalTrays,
  items,
  assignmentInheritedFromPlan,
  deliveryPlanName,
  inheritedVehicleNumber,
  inheritedDriverName,
  inheritedDriverPhone,
  inheritedDriverUserId,
  inheritedExpectedDeliveryDate,
  open,
  onOpenChange,
}: {
  id: string;
  mode: string;
  customerName?: string;
  orderNumber?: string;
  totalTrays?: number;
  items?: Array<{
    id: string;
    cropTypeName: string;
    variety?: string;
    unitName?: string;
    orderedQuantity: number;
    previouslyDeliveredQuantity: number;
    plannedQuantity: number;
    remainingAfterPlannedQuantity: number;
  }>;
  assignmentInheritedFromPlan?: boolean;
  deliveryPlanName?: string;
  inheritedVehicleNumber?: string;
  inheritedDriverName?: string;
  inheritedDriverPhone?: string;
  inheritedDriverUserId?: string;
  inheritedExpectedDeliveryDate?: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const notification = useNotification();
  const confirmAction = useConfirm();
  const complete = useCompleteDispatch();
  const { data: drivers = [] } = useDrivers();
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [selectedDriverUserId, setSelectedDriverUserId] = useState("");
  const [form, setForm] = useState({
    collectedByName: "",
    collectedByIdOrPhone: "",
    vehicleNumber: "",
    driverName: "",
    driverPhone: "",
    expectedDeliveryDate: "",
    notes: "",
  });

  const isDelivery = mode === "Delivery";
  const isInheritedPlanAssignment = isDelivery && !!assignmentInheritedFromPlan;

  useEffect(() => {
    if (!open || !isInheritedPlanAssignment) return;

    setForm((current) => ({
      ...current,
      vehicleNumber: inheritedVehicleNumber ?? current.vehicleNumber,
      driverName: inheritedDriverName ?? current.driverName,
      driverPhone: inheritedDriverPhone ?? current.driverPhone,
      expectedDeliveryDate: current.expectedDeliveryDate || (inheritedExpectedDeliveryDate ? inheritedExpectedDeliveryDate.slice(0, 10) : ""),
    }));

    setSelectedDriverUserId(inheritedDriverUserId ?? "");
  }, [
    open,
    isInheritedPlanAssignment,
    inheritedVehicleNumber,
    inheritedDriverName,
    inheritedDriverPhone,
    inheritedDriverUserId,
    inheritedExpectedDeliveryDate,
  ]);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleDriverSelect = (driverId: string) => {
    setSelectedDriverId(driverId);
    const d = drivers.find((x) => x.id === driverId);
    if (d) {
      setForm((f) => ({
        ...f,
        driverName: getDriverDisplayName(d),
        driverPhone: d.phone,
        vehicleNumber: f.vehicleNumber || d.defaultVehicleReg || "",
      }));
      // Auto-populate DriverUserId from the driver's linked account
      setSelectedDriverUserId(d.userId ?? "");
    }
  };

  const handleSubmit = async () => {
    const selectedDriver = drivers.find((x) => x.id === selectedDriverId);
    const resolvedDriverName = isInheritedPlanAssignment
      ? (inheritedDriverName ?? form.driverName).trim()
      : form.driverName.trim() || getDriverDisplayName(selectedDriver);
    const resolvedDriverPhone = isInheritedPlanAssignment
      ? (inheritedDriverPhone ?? form.driverPhone).trim()
      : form.driverPhone.trim() || selectedDriver?.phone?.trim() || "";
    const resolvedVehicleNumber = isInheritedPlanAssignment
      ? (inheritedVehicleNumber ?? form.vehicleNumber).trim()
      : form.vehicleNumber.trim();
    const resolvedExpectedDate = form.expectedDeliveryDate || (inheritedExpectedDeliveryDate ? inheritedExpectedDeliveryDate.slice(0, 10) : "");

    confirmAction({
      title: isDelivery ? "Complete dispatch for delivery?" : "Release for collection?",
      description: isDelivery
        ? "A delivery note will be generated and the goods marked out for delivery."
        : "The goods will be released to the collector.",
      confirmLabel: isDelivery ? "Complete dispatch" : "Release",
      body: (
        <div className="space-y-0.5">
          {customerName && (
            <p className="flex justify-between">
              <span className="text-muted-foreground">Customer</span>
              <span className="font-medium">{customerName}</span>
            </p>
          )}
          {orderNumber && (
            <p className="flex justify-between">
              <span className="text-muted-foreground">Order</span>
              <span className="font-medium">{orderNumber}</span>
            </p>
          )}
          {totalTrays != null && (
            <p className="flex justify-between">
              <span className="text-muted-foreground">Total trays</span>
              <span className="font-medium">{totalTrays}</span>
            </p>
          )}
          {isDelivery && resolvedDriverName && (
            <p className="flex justify-between">
              <span className="text-muted-foreground">Driver</span>
              <span className="font-medium">{resolvedDriverName}</span>
            </p>
          )}
          {isDelivery && form.vehicleNumber && (
            <p className="flex justify-between">
              <span className="text-muted-foreground">Vehicle</span>
              <span className="font-medium">{resolvedVehicleNumber}</span>
            </p>
          )}
          {!isDelivery && form.collectedByName && (
            <p className="flex justify-between">
              <span className="text-muted-foreground">Collected by</span>
              <span className="font-medium">{form.collectedByName}</span>
            </p>
          )}
        </div>
      ),
      onConfirm: async () => {
        try {
          await complete.mutateAsync({
            id,
            data: {
              collectedByName: form.collectedByName || undefined,
              collectedByIdOrPhone: form.collectedByIdOrPhone || undefined,
              vehicleNumber: isInheritedPlanAssignment ? undefined : (resolvedVehicleNumber || undefined),
              driverName: isInheritedPlanAssignment ? undefined : (resolvedDriverName || undefined),
              driverPhone: isInheritedPlanAssignment ? undefined : (resolvedDriverPhone || undefined),
              expectedDeliveryDate: resolvedExpectedDate || undefined,
              notes: form.notes || undefined,
              driverUserId: (isInheritedPlanAssignment ? inheritedDriverUserId : selectedDriverUserId) || undefined,
            },
          });
          notification.success("Dispatch completed — delivery note generated");
          onOpenChange(false);
        } catch (err) {
          notification.error("Failed to complete dispatch");
          throw err;
        }
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Complete Dispatch</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {isDelivery ? (
            <>
              {isInheritedPlanAssignment && (
                <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
                  Assignment inherited from {deliveryPlanName ? `delivery plan ${deliveryPlanName}` : "delivery plan"}. Use Reassign Delivery Plan to change driver or vehicle.
                </div>
              )}

              {isInheritedPlanAssignment && items && items.length > 0 && (
                <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                  <p className="text-sm font-medium">Transport Manager allocation (locked)</p>
                  {items.map((item) => (
                    <div key={item.id} className="rounded border bg-background px-2 py-2 text-xs">
                      <p className="font-medium">
                        {item.cropTypeName}{item.variety ? ` · ${item.variety}` : ""}
                      </p>
                      <p className="text-muted-foreground">
                        Ordered: {item.orderedQuantity} · Previously Delivered: {item.previouslyDeliveredQuantity} · Planned: {item.plannedQuantity} · Remaining: {item.remainingAfterPlannedQuantity} {getUnitLabel(item.unitName).toLowerCase()}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
                <div className="space-y-1">
                  <Label>Vehicle Number *</Label>
                  <Input
                    value={isInheritedPlanAssignment ? (inheritedVehicleNumber ?? form.vehicleNumber) : form.vehicleNumber}
                    onChange={set("vehicleNumber")}
                    placeholder="KAA 123A"
                    disabled={isInheritedPlanAssignment}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Expected Delivery Date *</Label>
                  <DatePicker
                    value={form.expectedDeliveryDate}
                    onChange={(v) => setForm((f) => ({ ...f, expectedDeliveryDate: v }))}
                    disabled={isInheritedPlanAssignment}
                  />
                </div>
              </div>
              {!isInheritedPlanAssignment && (
                <div className="space-y-1">
                  <Label>Select Driver</Label>
                  <Select value={selectedDriverId} onValueChange={handleDriverSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a driver…" />
                    </SelectTrigger>
                    <SelectContent>
                      {drivers.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.fullName} — {d.phone}
                          {d.defaultVehicleReg ? ` (${d.defaultVehicleReg})` : ""}
                          {d.userId ? " ✓ Mobile" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedDriverUserId && (
                    <p className="text-xs text-muted-foreground">
                      Mobile trip tracking enabled for this driver.
                    </p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
                <div className="space-y-1">
                  <Label>Driver Name *</Label>
                  <Input
                    value={isInheritedPlanAssignment ? (inheritedDriverName ?? form.driverName) : form.driverName}
                    onChange={set("driverName")}
                    placeholder="Auto-filled from selection"
                    disabled={isInheritedPlanAssignment}
                  />
                  <p className="text-xs text-muted-foreground">Auto-filled from selected driver. Edit only if needed.</p>
                </div>
                <div className="space-y-1">
                  <Label>Driver Phone *</Label>
                  <Input
                    value={isInheritedPlanAssignment ? (inheritedDriverPhone ?? form.driverPhone) : form.driverPhone}
                    onChange={set("driverPhone")}
                    placeholder="Auto-filled from selection"
                    disabled={isInheritedPlanAssignment}
                  />
                  <p className="text-xs text-muted-foreground">Auto-filled from selected driver. Edit only if needed.</p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-1">
                <Label>Collected By (Name) *</Label>
                <Input value={form.collectedByName} onChange={set("collectedByName")} />
              </div>
              <div className="space-y-1">
                <Label>Collector ID / Phone *</Label>
                <Input value={form.collectedByIdOrPhone} onChange={set("collectedByIdOrPhone")} />
              </div>
            </>
          )}
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={set("notes")} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={complete.isPending}>
            {complete.isPending ? "Processing…" : "Complete Dispatch"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Confirm Delivery Dialog ────────────────────────────────────────────────
function ConfirmDeliveryDialog({
  id,
  customerName,
  orderNumber,
  open,
  onOpenChange,
}: {
  id: string;
  customerName?: string;
  orderNumber?: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const notification = useNotification();
  const confirm = useConfirmDelivery();
  const confirmAction = useConfirm();
  const [deliveredToName, setDeliveredToName] = useState("");
  const [actualDeliveryDate, setActualDeliveryDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");

  const handleSubmit = () => {
    confirmAction({
      title: "Confirm delivery?",
      description: "This dispatch will be marked as delivered.",
      confirmLabel: "Confirm delivery",
      body: (
        <div className="space-y-0.5">
          {customerName && (
            <p className="flex justify-between">
              <span className="text-muted-foreground">Customer</span>
              <span className="font-medium">{customerName}</span>
            </p>
          )}
          {orderNumber && (
            <p className="flex justify-between">
              <span className="text-muted-foreground">Order</span>
              <span className="font-medium">{orderNumber}</span>
            </p>
          )}
          <p className="flex justify-between">
            <span className="text-muted-foreground">Delivery date</span>
            <span className="font-medium">{actualDeliveryDate}</span>
          </p>
          {deliveredToName && (
            <p className="flex justify-between">
              <span className="text-muted-foreground">Delivered to</span>
              <span className="font-medium">{deliveredToName}</span>
            </p>
          )}
        </div>
      ),
      onConfirm: async () => {
        try {
          await confirm.mutateAsync({
            id,
            data: { actualDeliveryDate, deliveredToName: deliveredToName || undefined, notes: notes || undefined },
          });
          notification.success("Delivery confirmed");
          onOpenChange(false);
        } catch (err) {
          notification.error("Failed to confirm delivery");
          throw err;
        }
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Confirm Delivery</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Actual Delivery Date *</Label>
            <DatePicker value={actualDeliveryDate} onChange={setActualDeliveryDate} />
          </div>
          <div className="space-y-1">
            <Label>Delivered To</Label>
            <Input value={deliveredToName} onChange={(e) => setDeliveredToName(e.target.value)} placeholder="Name at delivery point" />
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={confirm.isPending}>
            {confirm.isPending ? "Confirming…" : "Confirm Delivery"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Record Tray Return Dialog ──────────────────────────────────────────────
function RecordTrayReturnDialog({
  id,
  customerName,
  orderNumber,
  items,
  open,
  onOpenChange,
}: {
  id: string;
  customerName?: string;
  orderNumber?: string;
  items?: Array<{ id: string; cropTypeName: string; variety?: string; traysDispatched: number; unitName?: string; seedlingCount?: number | null }>;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const notification = useNotification();
  const confirmAction = useConfirm();
  const recordReturn = useRecordTrayReturn();
  const [quantity, setQuantity] = useState("");
  const [returnDate, setReturnDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");

  const handleSubmit = () => {
    const qty = parseInt(quantity);
    if (!qty || qty <= 0) {
      notification.error("Enter a valid quantity");
      return;
    }
    confirmAction({
      title: "Record tray return?",
      description: `${qty} tray(s) will be recorded as returned${customerName ? ` for ${customerName}` : ""}.`,
      confirmLabel: "Record return",
      body: (
        <div className="space-y-2">
          {(customerName || orderNumber) && (
            <div className="space-y-0.5">
              {customerName && (
                <p className="flex justify-between">
                  <span className="text-muted-foreground">Customer</span>
                  <span className="font-medium">{customerName}</span>
                </p>
              )}
              {orderNumber && (
                <p className="flex justify-between">
                  <span className="text-muted-foreground">Order</span>
                  <span className="font-medium">{orderNumber}</span>
                </p>
              )}
              <p className="flex justify-between">
                <span className="text-muted-foreground">Trays returned</span>
                <span className="font-medium">{qty}</span>
              </p>
            </div>
          )}
          {items && items.length > 0 && (
            <div className="border-t pt-2">
              <p className="text-muted-foreground mb-1">Dispatched</p>
              <ul className="space-y-0.5">
                {items.map((it) => (
                  <li key={it.id} className="flex justify-between gap-4">
                    <span>
                      {it.cropTypeName}
                      {it.variety ? ` · ${it.variety}` : ""}
                    </span>
                    <span className="tabular-nums text-right">
                      {it.traysDispatched} {it.unitName ?? "trays"}
                      {it.seedlingCount != null && (
                        <span className="text-muted-foreground"> · {it.seedlingCount.toLocaleString()} seedlings</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ),
      onConfirm: async () => {
        try {
          await recordReturn.mutateAsync({
            id,
            data: { dispatchRecordId: id, returnDate, quantityReturned: qty, notes: notes || undefined },
          });
          notification.success("Tray return recorded");
          onOpenChange(false);
          setQuantity("");
          setNotes("");
        } catch (err) {
          notification.error("Failed to record tray return");
          throw err;
        }
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Record Tray Return</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground bg-muted/50 border rounded px-3 py-2">
          Returned trays require storekeeper approval before being credited back to store inventory.
        </p>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Return Date *</Label>
            <DatePicker value={returnDate} onChange={(v) => setReturnDate(v)} />
          </div>
          <div className="space-y-1">
            <Label>Quantity Returned *</Label>
            <Input type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={recordReturn.isPending}>
            {recordReturn.isPending ? "Recording…" : "Record Return"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Raise Compensation Note Dialog ────────────────────────────────────────
function RaiseCompensationDialog({
  dispatchRecordId,
  customerName,
  items,
  open,
  onOpenChange,
}: {
  dispatchRecordId: string;
  customerName?: string;
  items: Array<{ id: string; cropTypeName: string; variety?: string; traysDispatched: number; unitName?: string }>;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const notification = useNotification();
  const confirmAction = useConfirm();
  const raise = useRaiseCompensation();
  const [itemId, setItemId] = useState("");
  const [affectedTrayCount, setAffectedTrayCount] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  const handleSubmit = () => {
    if (!itemId) { notification.error("Select a dispatch item"); return; }
    const count = parseInt(affectedTrayCount);
    if (!count || count <= 0) { notification.error("Enter valid tray count"); return; }
    if (!reason.trim()) { notification.error("Reason is required"); return; }
    const selectedItem = items.find((it) => it.id === itemId);
    confirmAction({
      title: "Raise compensation note?",
      description: `A compensation note for ${count} affected tray(s) will be raised.`,
      confirmLabel: "Raise note",
      body: (
        <div className="space-y-0.5">
          {customerName && (
            <p className="flex justify-between">
              <span className="text-muted-foreground">Customer</span>
              <span className="font-medium">{customerName}</span>
            </p>
          )}
          {selectedItem && (
            <p className="flex justify-between">
              <span className="text-muted-foreground">Item</span>
              <span className="font-medium">
                {selectedItem.cropTypeName}
                {selectedItem.variety ? ` · ${selectedItem.variety}` : ""}
              </span>
            </p>
          )}
          <p className="flex justify-between">
            <span className="text-muted-foreground">Affected trays</span>
            <span className="font-medium">{count}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Reason</span>
            <span className="font-medium">{reason.trim()}</span>
          </p>
        </div>
      ),
      onConfirm: async () => {
        try {
          await raise.mutateAsync({
            dispatchRecordId,
            dispatchRecordItemId: itemId,
            affectedTrayCount: count,
            reason,
            notes: notes || undefined,
          });
          notification.success("Compensation note raised");
          onOpenChange(false);
          setItemId(""); setAffectedTrayCount(""); setReason(""); setNotes("");
        } catch (err) {
          notification.error("Failed to raise compensation note");
          throw err;
        }
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Raise Compensation Note</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Affected Item *</Label>
            <Select value={itemId} onValueChange={setItemId}>
              <SelectTrigger>
                <SelectValue placeholder="— Select item —" />
              </SelectTrigger>
              <SelectContent>
                {items.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.cropTypeName}{item.variety ? ` (${item.variety})` : ""} — {item.traysDispatched} {getUnitLabel(item.unitName)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Affected Tray Count *</Label>
            <Input type="number" min={1} value={affectedTrayCount} onChange={(e) => setAffectedTrayCount(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Reason *</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explain the shortfall…" rows={3} />
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={raise.isPending}>
            {raise.isPending ? "Raising…" : "Raise Note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Route Guidance Section ─────────────────────────────────────────────────

const guidanceModeBadge = (mode: string) => {
  const variants: Record<string, string> = {
    Planned: "secondary",
    ActiveReroute: "default",
    DestinationOnly: "outline",
    Completed: "default",
  };
  return <Badge variant={(variants[mode] ?? "secondary") as any}>{mode}</Badge>;
};

const RouteGuidanceSection = ({ dispatchRecordId }: { dispatchRecordId: string }) => {
  const notify = useNotification();
  const [snapshot, setSnapshot] = useState<TripRouteSnapshotDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [templates, setTemplates] = useState<RouteTemplateSummaryDto[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    tripGuidanceApi.getSnapshot(dispatchRecordId)
      .then(setSnapshot)
      .catch(() => setSnapshot(null))
      .finally(() => setLoading(false));
  }, [dispatchRecordId]);

  useEffect(() => {
    if (assignOpen && templates.length === 0) {
      routeTemplatesApi.getActive().then((t) => setTemplates(t));
    }
  }, [assignOpen, templates.length]);

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const req: AssignRouteToTripRequest = {
        dispatchRecordId,
        routeTemplateId: selectedTemplate,
        stopOrderOverrideReason: overrideReason.trim() || undefined,
      };
      const result = await tripGuidanceApi.assignRoute(req);
      setSnapshot(result);
      setAssignOpen(false);
      notify.success("Route assigned successfully");
    } catch {
      notify.error("Failed to assign route");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm">Route Guidance</CardTitle>
        {!snapshot && (
          <PermissionGate permissions={["dispatch.complete"]}>
            <Button size="sm" variant="outline" onClick={() => setAssignOpen(true)}>
              Assign Route
            </Button>
          </PermissionGate>
        )}
      </CardHeader>
      <CardContent>
        {!snapshot ? (
          <p className="text-sm text-muted-foreground">No route assigned to this trip.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-muted-foreground">Route:</span>
              <span className="font-medium">{snapshot.routeName}</span>
              <span className="text-muted-foreground">Mode:</span>
              {guidanceModeBadge(snapshot.guidanceMode)}
              {snapshot.autoRerouteCount > 0 && (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  {snapshot.autoRerouteCount} reroute{snapshot.autoRerouteCount !== 1 ? "s" : ""}
                </Badge>
              )}
            </div>
            {snapshot.scheduleOverrideReason && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                Stop-order override: {snapshot.scheduleOverrideReason}
              </p>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Stop</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Completed At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {snapshot.stops
                  .slice()
                  .sort((a, b) => a.sequence - b.sequence)
                  .map((stop) => (
                    <TableRow key={stop.id}>
                      <TableCell>{stop.sequence}</TableCell>
                      <TableCell>{stop.stopName}</TableCell>
                      <TableCell>
                        {stop.isCompleted ? (
                          <Badge variant="default">Done</Badge>
                        ) : (
                          <Badge variant="secondary">Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {stop.completedAt
                          ? new Date(stop.completedAt).toLocaleString("en-KE", {
                              dateStyle: "short",
                              timeStyle: "short",
                            })
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Assign Route Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Route Template</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAssign} className="space-y-4 pt-2">
            <div className="space-y-1">
              <Label>Route Template *</Label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select a template…" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} ({t.stopCount} stops)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Stop-Order Override Reason <span className="text-muted-foreground text-xs">(optional — requires override permission)</span></Label>
              <Input
                placeholder="Leave blank to use the template's stop order policy"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAssignOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!selectedTemplate || saving}>
                {saving ? "Assigning…" : "Assign Route"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ── Main Detail Page ───────────────────────────────────────────────────────

export const DispatchDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: record, isLoading } = useDispatchById(id);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [trayReturnOpen, setTrayReturnOpen] = useState(false);
  const [compensationOpen, setCompensationOpen] = useState(false);

  if (isLoading) return <p className="p-4 text-sm text-muted-foreground">Loading…</p>;
  if (!record) return <p className="p-4 text-sm text-destructive">Dispatch record not found.</p>;

  const canComplete = record.status === "ReadyForRelease";
  const canConfirmDelivery = record.status === "Dispatched" && record.fulfilmentMode === "Delivery";
  const canRecordReturn = record.requiresTrayReturn && (record.status === "Dispatched" || record.status === "Delivered") && (record.traysOutstanding ?? 0) > 0;
  const canRaiseCompensation = canComplete || canRecordReturn;
  const handoverPending = !record.handedOverAt && !record.acknowledgedAt;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/dispatch?tab=manager&managerTab=release")}
            className="mb-1 -ml-2"
          >
            ← Back
          </Button>
          <h1 className="text-xl font-bold font-mono">{record.dispatchNumber}</h1>
          <p className="text-sm text-muted-foreground">Order: {record.orderNumber} — {record.customerName}</p>
        </div>
        <div className="flex items-center gap-2">
          {statusBadge(record.status)}
          <PermissionGate permissions={["dispatch.print_delivery_note"]}>
            {record.deliveryNoteNumber && (
              <Button variant="outline" size="sm" onClick={() => dispatchApi.downloadDeliveryNotePdf(id!)}>
                Download Delivery Note
              </Button>
            )}
          </PermissionGate>
        </div>
      </div>

      {/* Action bar */}
      <div className="flex gap-2 flex-wrap">
        <PermissionGate permissions={["dispatch.complete"]}>
          {canComplete && (
            <Button onClick={() => setCompleteOpen(true)}>Complete Dispatch</Button>
          )}
        </PermissionGate>
        <PermissionGate permissions={["dispatch.confirm_delivery"]}>
          {canConfirmDelivery && (
            <Button onClick={() => setDeliveryOpen(true)}>Confirm Delivery</Button>
          )}
        </PermissionGate>
        <PermissionGate permissions={["dispatch.record_tray_return"]}>
          {canRecordReturn && (
            <Button variant="outline" onClick={() => setTrayReturnOpen(true)}>Record Tray Return</Button>
          )}
        </PermissionGate>
        <PermissionGate permissions={["dispatch.raise_compensation"]}>
          {canRaiseCompensation && (
            <Button variant="outline" onClick={() => setCompensationOpen(true)}>Raise Compensation Note</Button>
          )}
        </PermissionGate>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Fulfilment Mode", value: record.fulfilmentMode },
          { label: record.items.every(i => i.unit === "Pieces" || i.unitName === "Pieces") ? "Pieces Dispatched" : "Trays Dispatched", value: (record.totalTraysDispatched ?? 0).toString() },
          ...(record.requiresTrayReturn ? [
            { label: "Trays Returned", value: (record.totalTraysReturned ?? 0).toString() },
            { label: "Outstanding", value: (record.traysOutstanding ?? 0).toString() },
          ] : []),
        ].map((c) => (
          <Card key={c.label}>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground">{c.label}</p>
              <p className="text-lg font-semibold">{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Payment summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {[
          { label: "Order Total", value: formatKES(record.orderTotalAmount) },
          { label: "Amount Paid", value: formatKES(record.amountPaid) },
          { label: "Balance", value: formatKES(record.balanceAmount) },
        ].map((c) => (
          <Card key={c.label}>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground">{c.label}</p>
              <p className="text-lg font-semibold">{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Level 1 — Handover info */}
      {record.assignmentInheritedFromPlan && (
        <Card className="border-sky-200 bg-sky-50/40">
          <CardHeader><CardTitle className="text-sm">Transport Plan Allocation</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">Plan: {record.deliveryPlanName ?? "Published plan"}</Badge>
              <Badge variant="outline">Route: {record.plannedRouteName ?? "Not assigned"}</Badge>
              <Badge variant="outline">Driver: {record.driverName ?? "Not assigned"}</Badge>
              <Badge variant="outline">Vehicle: {record.vehicleNumber ?? "Not assigned"}</Badge>
            </div>
            {record.items.map((item) => (
              <div key={item.id} className="rounded-md border bg-background p-2">
                <p className="font-medium">{item.cropTypeName}{item.variety ? ` · ${item.variety}` : ""}</p>
                <p className="text-muted-foreground">
                  Ordered: {item.orderedQuantity} · Previously Delivered: {item.previouslyDeliveredQuantity} · Planned: {item.plannedQuantity} · Remaining: {item.remainingAfterPlannedQuantity}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Level 1 — Handover info */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Level 1 — Growing Supervisor Handover</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-2 text-sm">
          <div><span className="text-muted-foreground">Handed Over By:</span> {record.handedOverByUserName ?? (handoverPending ? "Pending confirmation" : "—")}</div>
          <div><span className="text-muted-foreground">At:</span> {record.handedOverAt ? formatDateTime(record.handedOverAt) : (handoverPending ? "Pending confirmation" : "—")}</div>
          <div><span className="text-muted-foreground">Acknowledged By:</span> {record.acknowledgedByUserName ?? (handoverPending ? "Pending acknowledgement" : "—")}</div>
          <div><span className="text-muted-foreground">At:</span> {record.acknowledgedAt ? formatDateTime(record.acknowledgedAt) : (handoverPending ? "Pending acknowledgement" : "—")}</div>
          {record.handoverNotes && <div className="col-span-2"><span className="text-muted-foreground">Notes:</span> {record.handoverNotes}</div>}
        </CardContent>
      </Card>

      {/* Level 2 — Dispatch to customer */}
      {record.dispatchedAt && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Level 2 — Dispatch to Customer</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-muted-foreground">Dispatched By:</span> {record.dispatchedByUserName ?? "—"}</div>
            <div><span className="text-muted-foreground">At:</span> {formatDateTime(record.dispatchedAt)}</div>
            {record.fulfilmentMode === "Delivery" ? (
              <>
                {record.deliveryPlanName && (
                  <div className="col-span-2"><span className="text-muted-foreground">Source Plan:</span> {record.deliveryPlanName} {record.assignmentInheritedFromPlan ? "(inherited assignment)" : ""}</div>
                )}
                <div><span className="text-muted-foreground">Vehicle:</span> {record.vehicleNumber ?? "—"}</div>
                <div><span className="text-muted-foreground">Driver:</span> {record.driverName ?? "—"} {record.driverPhone ? `(${record.driverPhone})` : ""}</div>
                <div><span className="text-muted-foreground">Expected Delivery:</span> {formatDate(record.expectedDeliveryDate)}</div>
                <div><span className="text-muted-foreground">Actual Delivery:</span> {formatDate(record.actualDeliveryDate)}</div>
                {record.deliveredToName && <div><span className="text-muted-foreground">Delivered To:</span> {record.deliveredToName}</div>}
              </>
            ) : (
              <>
                <div><span className="text-muted-foreground">Collected By:</span> {record.collectedByName ?? "—"}</div>
                <div><span className="text-muted-foreground">ID/Phone:</span> {record.collectedByIdOrPhone ?? "—"}</div>
              </>
            )}
            {record.deliveryNoteNumber && (
              <div className="col-span-2">
                <span className="text-muted-foreground">Delivery Note:</span>{" "}
                <span className="font-mono">{record.deliveryNoteNumber}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payments */}
      {record.payments.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Payments</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payment #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Receipt / Ref</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {record.payments.map((p) => (
                  <TableRow key={p.paymentNumber}>
                    <TableCell className="font-mono text-xs">{p.paymentNumber}</TableCell>
                    <TableCell>{formatDate(p.paymentDate)}</TableCell>
                    <TableCell>{p.paymentMethod}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {p.receiptNumber ?? p.paymentReference ?? "—"}
                    </TableCell>
                    <TableCell className="text-right font-semibold">{formatKES(p.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Route Guidance */}
      <RouteGuidanceSection dispatchRecordId={id!} />

      {/* Line items */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Dispatch Items</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Crop</TableHead>
                <TableHead>Variety</TableHead>
                <TableHead>Tray Type</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead className="text-right">Seedlings</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {record.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.cropTypeName}</TableCell>
                  <TableCell>{item.variety ?? "—"}</TableCell>
                  <TableCell>{formatTrayType(item.trayType)}</TableCell>
                  <TableCell>{getUnitLabel(item.unitName)}</TableCell>
                  <TableCell className="text-right">{item.traysDispatched}</TableCell>
                  <TableCell className="text-right">
                    {item.seedlingCount != null ? item.seedlingCount.toLocaleString() : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Tray returns */}
      {record.trayReturns.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Tray Returns</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Qty Returned</TableHead>
                  <TableHead>Recorded By</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {record.trayReturns.map((tr) => (
                  <TableRow key={tr.id}>
                    <TableCell>{formatDate(tr.returnDate)}</TableCell>
                    <TableCell className="text-right">{tr.quantityReturned}</TableCell>
                    <TableCell>{tr.recordedByUserName}</TableCell>
                    <TableCell>{tr.notes ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      {id && (
        <>
          <CompleteDispatchDialog
            id={id}
            mode={record.fulfilmentMode}
            customerName={record.customerName}
            orderNumber={record.orderNumber}
            totalTrays={record.totalTraysDispatched}
            items={record.items}
            assignmentInheritedFromPlan={record.assignmentInheritedFromPlan}
            deliveryPlanName={record.deliveryPlanName}
            inheritedVehicleNumber={record.vehicleNumber}
            inheritedDriverName={record.driverName}
            inheritedDriverPhone={record.driverPhone}
            inheritedDriverUserId={record.driverUserId}
            inheritedExpectedDeliveryDate={record.expectedDeliveryDate}
            open={completeOpen}
            onOpenChange={setCompleteOpen}
          />
          <ConfirmDeliveryDialog
            id={id}
            customerName={record.customerName}
            orderNumber={record.orderNumber}
            open={deliveryOpen}
            onOpenChange={setDeliveryOpen}
          />
          <RecordTrayReturnDialog
            id={id}
            customerName={record.customerName}
            orderNumber={record.orderNumber}
            items={record.items}
            open={trayReturnOpen}
            onOpenChange={setTrayReturnOpen}
          />
          <RaiseCompensationDialog
            dispatchRecordId={id}
            customerName={record.customerName}
            items={record.items}
            open={compensationOpen}
            onOpenChange={setCompensationOpen}
          />
        </>
      )}
    </div>
  );
};
